import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { demoCode, openDemo } from "@/lib/demo";
import { ConnexisMark } from "@/components/brand/ConnexisMark";
import { Handshake, Building2, Headset, UserRound, Megaphone, Loader2, ArrowRight, ShieldCheck } from "lucide-react";

const ICONS = { introducer: Handshake, lender: Building2, "call-centre": Headset, agent: UserRound, media: Megaphone };
const BODY = {
  introducer: "Submit a case, watch desks pick it up, follow every move and see what it pays.",
  lender: "Review the cases routed to you, move stages, ask the client for documents and send terms.",
  "call-centre": "Run the floor: every agent's cases, targets, the leaderboard and the company's earnings.",
  agent: "One agent's own seat — their cases and their share, nothing of the company's money.",
  media: "Tracked ad links per platform, clicks through to funded cases, spend, cost per case and ROAS.",
};

function useNoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
}

/** The private demo door — reachable only with the demo code in the link (?k=…). */
export default function DemoAccess() {
  useNoIndex();
  const { portal } = useParams();
  const [params] = useSearchParams();
  const code = params.get("k") || demoCode();
  const [portals, setPortals] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const enter = async (p) => {
    setBusy(p);
    try { await openDemo(code, p); }
    catch (e) { setError(e?.response?.data?.detail || "That demo portal didn't open — try again"); setBusy(null); }
  };

  useEffect(() => {
    if (!code) { setError("This demo link is missing its code — ask Connexis for a fresh one."); return; }
    api.get("/demo/config", { params: { k: code } })
      .then((r) => { setPortals(r.data.portals || []); if (portal) enter(portal); })
      .catch((e) => setError(e?.response?.data?.detail || "Demo access isn't available right now."));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const opening = portal && !error ? (portals || []).find((p) => p.key === portal) : null;

  return (
    <div className="min-h-screen bg-[#070B14] text-white relative overflow-hidden" data-testid="demo-access">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      <div className="absolute -top-32 right-0 w-[520px] h-[520px] rounded-full bg-[#0055FF]/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-5 py-14 sm:py-20">
        <ConnexisMark mode="dark" />
        <div className="mt-9">
          <div className="text-[10.5px] font-semibold tracking-[0.24em] uppercase text-[#7da9ff]">Connexis · live demo</div>
          <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em]">See the portals for real.</h1>
          <p className="mt-4 text-base text-white/55 max-w-2xl leading-relaxed">
            Every portal, working end to end, on made-up businesses. Click anything and try anything —
            nothing you do here contacts a real person, and one tap puts it all back.
          </p>
        </div>

        {error ? (
          <div className="mt-10 max-w-xl rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-[14px] text-white/75" data-testid="demo-error">{error}</div>
        ) : !portals || opening ? (
          <div className="mt-12 inline-flex items-center gap-3 text-white/70" data-testid="demo-opening">
            <Loader2 className="size-4 animate-spin" />
            {opening ? `Opening the ${opening.label.toLowerCase()}…` : "Getting the demo ready…"}
          </div>
        ) : (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {portals.map((p, i) => {
              const Icon = ICONS[p.key] || Handshake;
              return (
                <button key={p.key} onClick={() => enter(p.key)} disabled={!!busy}
                        style={{ animationDelay: `${i * 70}ms` }}
                        className="group text-left rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6 transition-[transform,background-color,border-color] duration-300 hover:border-[#0055FF]/50 hover:bg-white/[0.06] hover:-translate-y-1 disabled:opacity-60 animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
                        data-testid={`demo-enter-${p.key}`}>
                  <div className="grid place-items-center size-10 rounded-xl bg-[#0055FF]/15 border border-[#0055FF]/30 text-[#7da9ff] transition-transform duration-300 group-hover:scale-110">
                    <Icon className="size-4.5" />
                  </div>
                  <div className="mt-4 text-[17px] font-semibold">{p.label}</div>
                  <div className="mt-1 text-[12.5px] text-[#7da9ff]/80">{p.who}</div>
                  <p className="mt-2.5 text-[13px] text-white/50 leading-relaxed">{BODY[p.key]}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#7da9ff]">
                    {busy === p.key ? <Loader2 className="size-3.5 animate-spin" /> : <>Open the portal <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" /></>}
                  </div>
                </button>
              );
            })}
            <div className="rounded-3xl border border-white/[0.08] bg-white/[0.02] p-6 flex items-start gap-3.5">
              <div className="grid place-items-center size-9 rounded-xl bg-white/[0.05] border border-white/10 text-white/60 shrink-0">
                <ShieldCheck className="size-4" />
              </div>
              <div>
                <div className="text-[14px] font-semibold">A sandbox, not a copy</div>
                <p className="mt-1 text-[13px] text-white/50 leading-relaxed">
                  The demo runs on its own made-up data. No email, text or notification ever leaves it, and no real partner or client is ever shown.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
